import { assertEvent } from 'xstate';

import { type Guard, type Modification } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Determines if adding a new modification/portion for a specific ingredient
 * will exceed the maximum allowable aggregate quantity for its group.
 *
 * When paired extras exist and the included group is full, checks whether the
 * extras group still has capacity before blocking.
 */
export const checkIfExceedsMaxAggregateQuantity: Guard = function (args): boolean {
  const { context, event } = args;

  assertEvent(event, [
    'customization.add-modification',
    'customization.dressing.increase-portions',
  ]);

  const { ingredientId } = event;
  const {
    allModificationsMap,
    modifications,
    modificationLimits,
    extrasModificationsMap,
    isUpsellPricingEnabled,
  } = context;

  // ─── Missing Data ────────────────────────────────────────────────────

  const hasMissingData = !modifications || !allModificationsMap || !modificationLimits;

  if (hasMissingData) {
    return false;
  }

  // ─────────────────────────────────────────────────────────────────────

  const modification = allModificationsMap.get(ingredientId);

  if (!modification) {
    return false;
  }

  const { modificationGroupName } = modification;

  // ─── Multiple Quantity Allowed ───────────────────────────────────────

  const modificationGroupLimits = modificationLimits[modificationGroupName];
  const maxAggregateQuantity =
    modificationGroupLimits?.maxAggregateQuantity ?? Number.POSITIVE_INFINITY;

  // ─────────────────────────────────────────────────────────────────────

  const { active } = modifications;

  const matchingModifications = active.filter(
    (active) => active.modificationGroupName === modificationGroupName,
  );
  const matchingModificationsQuantity = matchingModifications.reduce(sumQuantity, 0);

  // ─── Check Updated Quantity ──────────────────────────────────────────

  const updatedQuantity = matchingModificationsQuantity + 1;
  const wouldExceedIncluded = updatedQuantity > maxAggregateQuantity;

  if (!wouldExceedIncluded) return false;

  // ─── Check Paired Extras Capacity ────────────────────────────────────

  if (!isUpsellPricingEnabled) return true;

  if (!modification.ingredientId) return true;

  const extrasModification = extrasModificationsMap.get(modification.ingredientId);

  if (!extrasModification) return true;

  const extrasGroupLimits = modificationLimits[extrasModification.modificationGroupName];

  if (!extrasGroupLimits) return true;

  const extrasActiveQuantity = active
    .filter((active) => active.modificationGroupName === extrasModification.modificationGroupName)
    .reduce(sumQuantity, 0);

  return extrasActiveQuantity + 1 > extrasGroupLimits.maxAggregateQuantity;
};

// ─────────────────────────────────────────────────────────────────────────────

function sumQuantity(total: number, modification: Modification) {
  return total + modification.quantity;
}
